import { useState, useEffect } from 'react';
import { MapPin, Truck, Check, ChevronDown, ChevronUp, Loader2, ExternalLink } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';

export default function DeliveryAddressSelector({
  selectedAddress,
  onAddressChange,
  subtotal = 0,
  token
}) {
  const [addresses, setAddresses] = useState([]);
  const [platformFees, setPlatformFees] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCustom, setShowCustom] = useState(false);
  const [selectedId, setSelectedId] = useState(selectedAddress || null);
  const [customForm, setCustomForm] = useState({ address_line1: '', city: '', postal_code: '', distance: '' });
  const [submittingCustom, setSubmittingCustom] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [addrRes, feeRes] = await Promise.all([
        fetch(`${API_URL}/api/delivery-addresses`),
        fetch(`${API_URL}/api/platform-fees`)
      ]);
      if (addrRes.ok) setAddresses((await addrRes.json()).filter(a => a.is_active));
      if (feeRes.ok) setPlatformFees(await feeRes.json());
    } catch (e) {
      console.error('Error loading delivery data:', e);
    } finally {
      setLoading(false);
    }
  };

  const isFreeDelivery = platformFees && subtotal >= platformFees.free_delivery_threshold;

  const calcFee = (distanceKm, baseFee) => {
    if (isFreeDelivery) return 0;
    if (baseFee !== undefined && baseFee !== null) return baseFee;
    if (!platformFees) return 0;
    if (distanceKm > platformFees.max_delivery_distance_km) return platformFees.flat_fee_beyond_max || 150;
    return distanceKm * (platformFees.delivery_fee_per_km || 5);
  };

  const handleSelectAddress = (addr) => {
    const fee = calcFee(addr.distance_km || 0, addr.delivery_fee);
    const id = addr.address_id;
    setSelectedId(id);
    setShowCustom(false);
    onAddressChange({
      type: addr.delivery_fee === 0 && addr.distance_km === 0 ? 'pickup' : 'pre_approved',
      address_id: id,
      name: addr.name,
      full_address: [addr.address_line1, addr.city, addr.postal_code].filter(Boolean).join(', '),
      delivery_fee: fee,
      distance_km: addr.distance_km || 0
    });
  };

  const handleCustomSubmit = () => {
    if (!customForm.address_line1 || !customForm.city) {
      toast.error('Please enter street address and city');
      return;
    }
    const dist = parseFloat(customForm.distance) || 10;
    const fee = calcFee(dist, undefined);
    const fullAddr = [customForm.address_line1, customForm.city, customForm.postal_code].filter(Boolean).join(', ');
    setSelectedId('custom');
    onAddressChange({
      type: 'custom',
      address_id: null,
      name: 'Custom Delivery',
      full_address: fullAddr,
      delivery_fee: fee,
      distance_km: dist
    });
    toast.success('Delivery address confirmed');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
        <span className="ml-2 text-sm text-gray-500">Loading addresses...</span>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="delivery-address-selector">
      {/* Free delivery banner */}
      {platformFees && (
        <div className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium mb-3 ${
          isFreeDelivery
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-amber-50 text-amber-700 border border-amber-200'
        }`}>
          <Truck className="h-3.5 w-3.5 flex-shrink-0" />
          {isFreeDelivery
            ? 'Free delivery applied to your order!'
            : `Add R${(platformFees.free_delivery_threshold - subtotal).toFixed(2)} more for free delivery`
          }
        </div>
      )}

      {/* Pre-loaded addresses */}
      {addresses.map((addr) => {
        const fee = calcFee(addr.distance_km || 0, addr.delivery_fee);
        const isPickup = addr.delivery_fee === 0 && addr.distance_km === 0;
        const isSelected = selectedId === addr.address_id;

        return (
          <button
            key={addr.address_id}
            onClick={() => handleSelectAddress(addr)}
            className={`w-full flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${
              isSelected
                ? 'border-primary bg-primary/5'
                : 'border-gray-200 hover:border-gray-300 bg-white'
            }`}
            data-testid={`addr-${addr.address_id}`}
          >
            {/* Icon */}
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
              isSelected ? 'bg-primary' : isPickup ? 'bg-green-100' : 'bg-gray-100'
            }`}>
              {isSelected ? (
                <Check className="h-4 w-4 text-white" strokeWidth={3} />
              ) : isPickup ? (
                <MapPin className="h-4 w-4 text-green-600" />
              ) : (
                <Truck className="h-4 w-4 text-gray-500" />
              )}
            </div>

            {/* Details */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className={`font-semibold text-sm ${isSelected ? 'text-primary' : 'text-gray-900'}`}>
                  {addr.name}
                </p>
                {addr.is_default && (
                  <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">Default</span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-0.5 truncate">
                {addr.address_line1}, {addr.city}
              </p>
              {addr.distance_km > 0 && (
                <p className="text-xs text-gray-400 mt-0.5">{addr.distance_km.toFixed(1)} km from GC3H</p>
              )}
            </div>

            {/* Fee */}
            <div className="flex-shrink-0 text-right">
              {isFreeDelivery && !isPickup ? (
                <span className="text-xs text-green-600 font-bold">FREE</span>
              ) : (
                <span className={`text-sm font-bold ${fee === 0 ? 'text-green-600' : 'text-gray-900'}`}>
                  {fee === 0 ? 'FREE' : `R${fee.toFixed(2)}`}
                </span>
              )}
            </div>
          </button>
        );
      })}

      {/* Custom Address */}
      <button
        onClick={() => { setShowCustom(!showCustom); setSelectedId(showCustom ? selectedId : null); }}
        className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-all ${
          selectedId === 'custom'
            ? 'border-primary bg-primary/5'
            : 'border-gray-200 hover:border-gray-300 bg-white'
        }`}
      >
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
          selectedId === 'custom' ? 'bg-primary' : 'bg-purple-100'
        }`}>
          {selectedId === 'custom'
            ? <Check className="h-4 w-4 text-white" strokeWidth={3} />
            : <MapPin className="h-4 w-4 text-purple-600" />
          }
        </div>
        <div className="flex-1">
          <p className={`font-semibold text-sm ${selectedId === 'custom' ? 'text-primary' : 'text-gray-900'}`}>
            Different Address
          </p>
          <p className="text-xs text-gray-500 mt-0.5">Enter a custom delivery location</p>
        </div>
        {showCustom ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
      </button>

      {/* Custom address form */}
      <AnimatePresence>
        {showCustom && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="p-4 border-2 border-dashed border-gray-200 rounded-xl space-y-3 bg-gray-50">
              <div>
                <label className="text-xs font-semibold text-gray-700">Street Address *</label>
                <input
                  type="text"
                  placeholder="123 Main Street"
                  value={customForm.address_line1}
                  onChange={e => setCustomForm({ ...customForm, address_line1: e.target.value })}
                  className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-semibold text-gray-700">City *</label>
                  <input
                    type="text"
                    placeholder="Johannesburg"
                    value={customForm.city}
                    onChange={e => setCustomForm({ ...customForm, city: e.target.value })}
                    className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700">Postal Code</label>
                  <input
                    type="text"
                    placeholder="1685"
                    value={customForm.postal_code}
                    onChange={e => setCustomForm({ ...customForm, postal_code: e.target.value })}
                    className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700">Estimated Distance from Midrand (km)</label>
                <input
                  type="number"
                  placeholder="e.g. 15"
                  min="1"
                  value={customForm.distance}
                  onChange={e => setCustomForm({ ...customForm, distance: e.target.value })}
                  className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white"
                />
                {customForm.distance && platformFees && (
                  <p className="text-xs text-gray-500 mt-1">
                    Estimated fee: {isFreeDelivery ? <span className="text-green-600 font-medium">FREE</span> : <span className="font-medium">R{calcFee(parseFloat(customForm.distance) || 0, undefined).toFixed(2)}</span>}
                  </p>
                )}
              </div>
              {customForm.address_line1 && customForm.city && (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${customForm.address_line1}, ${customForm.city}, South Africa`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Verify on Google Maps
                </a>
              )}
              <button
                onClick={handleCustomSubmit}
                disabled={!customForm.address_line1 || !customForm.city}
                className="w-full h-10 bg-primary text-white rounded-lg text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Confirm This Address
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Selected confirmation */}
      {selectedId && (
        <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-xl">
          <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
          <p className="text-xs font-medium text-green-700">Delivery location confirmed</p>
        </div>
      )}
    </div>
  );
}
